import {
  ReferenceInput,
  TextInput,
  SelectInput,
  ArrayInput,
  SimpleFormIterator,
} from "@/components/admin";
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/use-mobile";
import { required, useRecordContext, useTranslate } from "ra-core";
import ImageEditorField from "../misc/ImageEditorField";
import { isLinkedinUrl } from "../misc/isLinkedInUrl";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company, Sale } from "../types";
import { sizes } from "./sizes";

const isUrl = (url: string) => {
  if (!url) return;
  const UrlRegex = new RegExp(
    /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/i,
  );
  if (!UrlRegex.test(url)) {
    return "Deve ser uma URL válida";
  }
};

export const CompanyInputs = () => {
  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col gap-4 p-1">
      <CompanyDisplayInputs />
      <div className={`flex gap-6 ${isMobile ? "flex-col" : "flex-row"}`}>
        <div className="flex flex-col gap-10 flex-1">
          <CompanyContactInputs />
          <CompanyContextInputs />
        </div>
        <Separator orientation={isMobile ? "horizontal" : "vertical"} />
        <div className="flex flex-col gap-8 flex-1">
          <CompanyAddressInputs />
          <CompanyAdditionalInformationInputs />
          <CompanyAccountManagerInput />
        </div>
      </div>
    </div>
  );
};

const CompanyDisplayInputs = () => {
  const record = useRecordContext<Company>();
  const translate = useTranslate();
  
  return (
    <div className="flex gap-4 flex-1 flex-row">
      <ImageEditorField
        source="logo"
        type="avatar"
        width={60}
        height={60}
        emptyText={record?.name.charAt(0)}
        linkPosition="bottom"
      />
      <TextInput
        source="name"
        label={translate("simplecrm.companies.name_field", { _: "Nome" })}
        className="w-full h-fit"
        validate={required()}
        helperText={false}
        placeholder={translate("simplecrm.companies.company_name", { _: "Nome da empresa" })}
      />
    </div>
  );
};

const CompanyContactInputs = () => {
  const translate = useTranslate();
  
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">{translate("simplecrm.companies.contact", { _: "Contato" })}</h6>
      <TextInput 
        source="website" 
        label={translate("simplecrm.companies.website", { _: "Website" })}
        helperText={false} 
        validate={isUrl} 
      />
      <TextInput
        source="linkedin_url"
        label={translate("simplecrm.companies.linkedin_url", { _: "URL do LinkedIn" })}
        helperText={false}
        validate={isLinkedinUrl}
      />
      <TextInput 
        source="phone_number" 
        label={translate("simplecrm.companies.phone_number", { _: "Número de telefone" })}
        helperText={false} 
      />
    </div>
  );
};

const CompanyContextInputs = () => {
  const { companySectors } = useConfigurationContext();
  const translate = useTranslate();
  
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">{translate("simplecrm.companies.context", { _: "Contexto" })}</h6>
      <SelectInput
        source="sector"
        label={translate("simplecrm.companies.sector", { _: "Setor" })}
        choices={companySectors.map((sector) => ({
          id: sector,
          name: sector,
        }))}
        helperText={false}
      />
      <SelectInput 
        source="size" 
        label={translate("simplecrm.companies.size", { _: "Tamanho" })}
        choices={sizes} 
        helperText={false} 
      />
      <TextInput 
        source="revenue" 
        label={translate("simplecrm.companies.revenue", { _: "Receita" })}
        helperText={false} 
      />
      <TextInput 
        source="tax_identifier" 
        label={translate("simplecrm.companies.tax_identifier", { _: "Identificador fiscal" })}
        helperText={false} 
      />
    </div>
  );
};

const CompanyAddressInputs = () => {
  const translate = useTranslate();
  
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">{translate("simplecrm.companies.address", { _: "Endereço" })}</h6>
      <TextInput 
        source="address" 
        label={translate("simplecrm.companies.address_field", { _: "Endereço" })}
        helperText={false} 
      />
      <TextInput 
        source="city" 
        label={translate("simplecrm.companies.city", { _: "Cidade" })}
        helperText={false} 
      />
      <TextInput 
        source="zipcode" 
        label={translate("simplecrm.companies.zipcode", { _: "CEP" })}
        helperText={false} 
      />
      <TextInput 
        source="stateAbbr" 
        label={translate("simplecrm.companies.state", { _: "Estado" })}
        helperText={false} 
      />
      <TextInput 
        source="country" 
        label={translate("simplecrm.companies.country", { _: "País" })}
        helperText={false} 
      />
    </div>
  );
};

const CompanyAdditionalInformationInputs = () => {
  const translate = useTranslate();
  
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">{translate("simplecrm.companies.additional_information", { _: "Informações adicionais" })}</h6>
      <TextInput 
        source="description" 
        label={translate("simplecrm.companies.description", { _: "Descrição" })}
        multiline 
        helperText={false} 
      />
      <ArrayInput 
        source="context_links" 
        label={translate("simplecrm.companies.context_links", { _: "Links de contexto" })}
        helperText={false}
      >
        <SimpleFormIterator disableReordering fullWidth getItemLabel={false}>
          <TextInput
            source=""
            label={false}
            helperText={false}
            validate={isUrl}
          />
        </SimpleFormIterator>
      </ArrayInput>
    </div>
  );
};

const CompanyAccountManagerInput = () => {
  const translate = useTranslate();
  
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">{translate("simplecrm.companies.account_manager", { _: "Gerente de Conta" })}</h6>
      <ReferenceInput
        source="sales_id"
        reference="sales"
        filter={{
          "disabled@neq": true,
        }}
      >
        <SelectInput
          label={translate("simplecrm.companies.account_manager", { _: "Gerente de Conta" })}
          helperText={false}
          optionText={saleOptionRenderer}
        />
      </ReferenceInput>
    </div>
  );
};

const saleOptionRenderer = (choice: Sale) =>
  `${choice.first_name} ${choice.last_name}`;
